import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Car as CarIcon, Gauge, CheckCircle2, KeySquare, Wrench, Search, ArrowUpDown, Camera, User, Calendar as CalendarIcon, Phone, Rocket } from "lucide-react";
import PhotoGallery from "@/components/PhotoGallery";
import PaymentStatusBadge from "@/components/PaymentStatusBadge";
import QuickRentalDialog from "@/components/QuickRentalDialog";
import { Link } from "react-router-dom";
import { format, parseISO, differenceInCalendarDays } from "date-fns";

const STATUS_VALUES = ["disponivel", "alugado", "manutencao"];
const STATUS_CLS = {
  disponivel: "bg-green-100 text-green-800 border-green-200",
  alugado: "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/30",
  manutencao: "bg-amber-100 text-amber-800 border-amber-200",
};
const FUELS = ["Gasolina", "Diesel", "Híbrido", "Elétrico"];

const empty = {
  model: "", plate: "", year: new Date().getFullYear(), color: "",
  photo_url: "", weekly_rate: 0, daily_price: 0, monthly_rate: 0,
  gps_device_id: "", current_mileage: 0,
  status: "disponivel", fuel_type: "Gasolina",
  insurance_policy: "", insurance_expiry: "",
  ma_inspection_expiry: "", registration_expiry: "",
  ezpass_tag: "",
  vin: "",
  notes: ""
};

export default function Frota() {
  const { t, fmtNumber, fmtCurrency } = useT();
  const [cars, setCars] = useState([]);
  const [activeByCar, setActiveByCar] = useState({}); // car_id -> {rental, customer}
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");
  const [quickCar, setQuickCar] = useState(null);

  const load = async () => {
    const [carsR, rentalsR, custsR] = await Promise.all([
      api.get("/cars"),
      api.get("/rentals", { params: { status: "ativo" } }),
      api.get("/customers"),
    ]);
    setCars(carsR.data);
    const custMap = Object.fromEntries((custsR.data || []).map((c) => [c.id, c]));
    const map = {};
    for (const r of rentalsR.data || []) {
      if (!map[r.car_id]) {
        map[r.car_id] = { rental: r, customer: custMap[r.customer_id] || null };
      }
    }
    // Enrich each active rental with closing-summary pending charges
    await Promise.all(Object.entries(map).map(async ([carId, entry]) => {
      try {
        const cs = await api.get(`/rentals/${entry.rental.id}/closing-summary`);
        entry.charges = {
          tolls: cs.data.tolls_total || 0,
          tickets: cs.data.tickets_total || 0,
          extras: cs.data.extras_total || 0,
        };
      } catch (e) {
        entry.charges = null;
      }
    }));
    setActiveByCar({ ...map });
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      model: c.model, plate: c.plate, year: c.year, color: c.color,
      photo_url: c.photo_url || "",
      weekly_rate: c.weekly_rate || (c.daily_price ? +(c.daily_price * 7).toFixed(2) : 0),
      daily_price: c.daily_price || (c.weekly_rate ? +(c.weekly_rate / 7).toFixed(2) : 0),
      monthly_rate: c.monthly_rate || (c.weekly_rate ? +(c.weekly_rate * 4.345).toFixed(2) : 0),
      gps_device_id: c.gps_device_id || "", current_mileage: c.current_mileage,
      status: c.status, fuel_type: c.fuel_type, notes: c.notes || "",
      insurance_policy: c.insurance_policy || "",
      insurance_expiry: c.insurance_expiry ? c.insurance_expiry.slice(0, 10) : "",
      ma_inspection_expiry: c.ma_inspection_expiry ? c.ma_inspection_expiry.slice(0, 10) : "",
      registration_expiry: c.registration_expiry ? c.registration_expiry.slice(0, 10) : "",
      ezpass_tag: c.ezpass_tag || "",
      vin: c.vin || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const weekly = Number(form.weekly_rate);
      const daily = Number(form.daily_price) || +(weekly / 7).toFixed(2);
      const monthly = Number(form.monthly_rate) || +(weekly * 4.345).toFixed(2);
      const payload = {
        ...form,
        year: Number(form.year),
        weekly_rate: weekly,
        daily_price: daily,
        monthly_rate: monthly,
        current_mileage: Number(form.current_mileage),
      };
      // Clean empty strings to null for optional date fields
      ["insurance_expiry", "ma_inspection_expiry", "registration_expiry", "insurance_policy", "photo_url", "gps_device_id", "ezpass_tag", "vin", "notes"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (editing) {
        await api.put(`/cars/${editing.id}`, payload);
        toast.success(t("fleet.updated"));
      } else {
        await api.post("/cars", payload);
        toast.success(t("fleet.created"));
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("fleet.confirmDelete"))) return;
    try {
      await api.delete(`/cars/${id}`);
      toast.success(t("fleet.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="frota-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">{t("fleet.pageTitle", { n: cars.length })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("fleet.pageSubtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white" data-testid="add-car-button">
              <Plus className="w-4 h-4 mr-2" /> {t("fleet.newCar")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? t("fleet.editCar") : t("fleet.registerCar")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <Field label={t("fleet.model")}><Input className="rounded-none" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="car-model-input" /></Field>
              <Field label={t("fleet.plate")}><Input className="rounded-none uppercase" required value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} data-testid="car-plate-input" /></Field>
              <Field label={t("fleet.year")}><Input type="number" className="rounded-none" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></Field><Field label={t("fleet.color")}><Input className="rounded-none" required value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>
              <Field label={t("fleet.vin")} full><Input className="rounded-none uppercase font-mono tracking-wider" maxLength={17} value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "") })} placeholder="Ex: 1HGCM82633A123456 (17 caracteres)" data-testid="fleet-vin" /></Field>
              <div className="col-span-2 border border-zinc-200 p-3 bg-zinc-50">
                <div className="overline mb-3">{t("fleet.ratesTitle")}</div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label={t("fleet.dailyPrice")}>
                    <Input type="number" step="0.01" className="rounded-none" value={form.daily_price}
                      onChange={(e) => {
                        const d = Number(e.target.value) || 0;
                        setForm((f) => ({
                          ...f,
                          daily_price: e.target.value,
                          weekly_rate: f.weekly_rate || +(d * 7).toFixed(2),
                          monthly_rate: f.monthly_rate || +(d * 30).toFixed(2),
                        }));
                      }}
                      data-testid="car-daily-rate-input" />
                  </Field>
                  <Field label={t("fleet.weeklyPrice")}>
                    <Input type="number" step="0.01" className="rounded-none" required value={form.weekly_rate}
                      onChange={(e) => {
                        const w = Number(e.target.value) || 0;
                        setForm((f) => ({
                          ...f,
                          weekly_rate: e.target.value,
                          daily_price: w > 0 ? +(w / 7).toFixed(2) : f.daily_price,
                          monthly_rate: w > 0 && !Number(f.monthly_rate) ? +(w * 4.345).toFixed(2) : f.monthly_rate,
                        }));
                      }}
                      data-testid="car-weekly-rate-input" />
                  </Field>
                  <Field label={t("fleet.monthlyPrice")}>
                    <Input type="number" step="0.01" className="rounded-none" value={form.monthly_rate}
                      onChange={(e) => setForm({ ...form, monthly_rate: e.target.value })}
                      data-testid="car-monthly-rate-input" />
                  </Field>
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-2">
                  {t("fleet.ratesHint")}
                </div>
              </div>
              <Field label={t("fleet.mileage")}><Input type="number" className="rounded-none" value={form.current_mileage} onChange={(e) => setForm({ ...form, current_mileage: e.target.value })} /></Field>
              <Field label={t("fleet.gpsId")}><Input className="rounded-none" value={form.gps_device_id} onChange={(e) => setForm({ ...form, gps_device_id: e.target.value })} placeholder="Ex: TK-2024-001" /></Field>
              <Field label={t("fleet.fuel")}>
                <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{FUELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("common.status")}>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_VALUES.map((s) => <SelectItem key={s} value={s}>{t(`fleet.status.${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("fleet.photo")} full><Input className="rounded-none" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." /></Field>
              <Field label={t("fleet.insurancePolicy")}><Input className="rounded-none" value={form.insurance_policy} onChange={(e) => setForm({ ...form, insurance_policy: e.target.value })} placeholder="POLICY #" /></Field>
              <Field label={t("fleet.insuranceExpiry")}><Input className="rounded-none" type="date" value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} data-testid="fleet-insurance-expiry" /></Field>
              <Field label={t("fleet.maInspectionExpiry")}><Input className="rounded-none" type="date" value={form.ma_inspection_expiry} onChange={(e) => setForm({ ...form, ma_inspection_expiry: e.target.value })} data-testid="fleet-inspection-expiry" /></Field>
              <Field label={t("fleet.registrationExpiry")}><Input className="rounded-none" type="date" value={form.registration_expiry} onChange={(e) => setForm({ ...form, registration_expiry: e.target.value })} data-testid="fleet-registration-expiry" /></Field>
              <Field label={t("fleet.ezpassTag")}><Input className="rounded-none uppercase font-mono" value={form.ezpass_tag} onChange={(e) => setForm({ ...form, ezpass_tag: e.target.value.toUpperCase() })} placeholder="Ex: 02100123456789" data-testid="fleet-ezpass-tag" /></Field>
              <Field label={t("common.notes")} full><Input className="rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
              {editing && (
                <div className="col-span-2 mt-2">
                  <PhotoGallery
                    ownerType="car"
                    ownerId={editing.id}
                    title={t("photos.carPhotos")}
                    description={t("photos.carPhotosHint")}
                    gridCols={4}
                  />
                </div>
              )}
              <DialogFooter className="col-span-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-car-button">{t("common.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + Sort controls */}
      {cars.length > 0 && (
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("fleet.searchPlaceholder")}
              className="rounded-none pl-9 h-10 border-zinc-300"
              data-testid="fleet-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-zinc-400" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="rounded-none w-56 h-10" data-testid="fleet-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="created_desc">{t("fleet.sort.newest")}</SelectItem>
                <SelectItem value="created_asc">{t("fleet.sort.oldest")}</SelectItem>
                <SelectItem value="plate_asc">{t("fleet.sort.plateAsc")}</SelectItem>
                <SelectItem value="model_asc">{t("fleet.sort.modelAsc")}</SelectItem>
                <SelectItem value="price_desc">{t("fleet.sort.priceDesc")}</SelectItem>
                <SelectItem value="price_asc">{t("fleet.sort.priceAsc")}</SelectItem>
                <SelectItem value="mileage_asc">{t("fleet.sort.mileageAsc")}</SelectItem>
                <SelectItem value="mileage_desc">{t("fleet.sort.mileageDesc")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Status counters + filter tabs */}
      {cars.length > 0 && (() => {
        const counts = {
          all: cars.length,
          disponivel: cars.filter((c) => c.status === "disponivel").length,
          alugado: cars.filter((c) => c.status === "alugado").length,
          manutencao: cars.filter((c) => c.status === "manutencao").length,
        };
        const TABS = [
          { key: "all", label: t("common.all"), icon: CarIcon, count: counts.all, color: "text-zinc-900" },
          { key: "disponivel", label: t("fleet.status.disponivel"), icon: CheckCircle2, count: counts.disponivel, color: "text-green-700" },
          { key: "alugado", label: t("fleet.status.alugado"), icon: KeySquare, count: counts.alugado, color: "text-[#F5A623]" },
          { key: "manutencao", label: t("fleet.status.manutencao"), icon: Wrench, count: counts.manutencao, color: "text-amber-700" },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-zinc-200" data-testid="fleet-filter-tabs">
            {TABS.map((tab) => {
              const active = filter === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  data-testid={`fleet-tab-${tab.key}`}
                  className={`border-r border-b border-zinc-200 -ml-px -mt-px p-5 text-left transition-colors ${
                    active ? "bg-black text-white" : "bg-white hover:bg-zinc-50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`overline ${active ? "text-white/60" : ""}`}>{tab.label}</div>
                    <tab.icon className={`w-4 h-4 ${active ? "text-[#F5A623]" : "text-zinc-400"}`} />
                  </div>
                  <div className={`font-display font-black text-4xl tracking-tighter ${active ? "text-[#F5A623]" : tab.color}`}>
                    {tab.count}
                  </div>
                </button>
              );
            })}
          </div>
        );
      })()}

      {cars.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center" data-testid="frota-empty">
          <CarIcon className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("fleet.emptyTitle")}</div>
          <p className="text-sm text-zinc-500 mb-6">{t("fleet.emptySub")}</p>
          <Button onClick={openCreate} className="rounded-none bg-[#F5A623] hover:bg-[#D97706]">
            <Plus className="w-4 h-4 mr-2" /> {t("fleet.registerFirst")}
          </Button>
        </div>
      ) : (() => {
        const q = search.trim().toLowerCase();
        const byFilter = filter === "all" ? cars : cars.filter((c) => c.status === filter);
        const bySearch = q
          ? byFilter.filter((c) =>
              [c.plate, c.model, c.color, c.year?.toString(), c.gps_device_id]
                .some((v) => (v || "").toString().toLowerCase().includes(q))
            )
          : byFilter;
        const sorters = {
          created_desc: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
          created_asc:  (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
          plate_asc:    (a, b) => (a.plate || "").localeCompare(b.plate || ""),
          model_asc:    (a, b) => (a.model || "").localeCompare(b.model || ""),
          price_desc:   (a, b) => (b.weekly_rate || (b.daily_price||0) * 7) - (a.weekly_rate || (a.daily_price||0) * 7),
          price_asc:    (a, b) => (a.weekly_rate || (a.daily_price||0) * 7) - (b.weekly_rate || (b.daily_price||0) * 7),
          mileage_asc:  (a, b) => (a.current_mileage || 0) - (b.current_mileage || 0),
          mileage_desc: (a, b) => (b.current_mileage || 0) - (a.current_mileage || 0),
        };
        const visible = [...bySearch].sort(sorters[sortBy] || sorters.created_desc);
        if (visible.length === 0) {
          return (
            <div className="border border-zinc-200 p-12 text-center bg-white" data-testid="fleet-filter-empty">
              <CarIcon className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
              <div className="overline">{q ? t("fleet.noResults") : t(`fleet.status.${filter}`) + " — 0"}</div>
              <p className="text-xs text-zinc-500 mt-2">{q ? t("fleet.tryDifferent") : "No vehicles in this status."}</p>
            </div>
          );
        }
        return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 border-l border-t border-zinc-200">
          {visible.map((c) => (
            <div key={c.id} className="border-r border-b border-zinc-200 -ml-px -mt-px bg-white" data-testid={`car-card-${c.plate}`}>
              <div className="aspect-[16/10] bg-zinc-100 relative overflow-hidden">
                {c.photo_url ? (
                  <img src={c.photo_url} alt={c.model} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <CarIcon className="w-10 h-10 text-zinc-300" />
                  </div>
                )}
                <div className={`absolute top-2 left-2 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${STATUS_CLS[c.status]}`}>{t(`fleet.status.${c.status}`)}</div>
                <ComplianceBadges car={c} />
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <div className="font-display font-bold tracking-tight text-sm leading-tight truncate">{c.model}</div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">{c.plate} · {c.year} · {c.color}</div>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1 text-zinc-600"><Gauge className="w-3 h-3" /> {fmtNumber(c.current_mileage)} mi</div>
                  <div className="font-mono font-semibold">{fmtCurrency(c.weekly_rate || (c.daily_price||0) * 7)}/{t("fleet.weekShort")}</div>
                </div>
                {activeByCar[c.id] && (
                  <ActiveRenterBlock active={activeByCar[c.id]} t={t} />
                )}
                {c.gps_device_id && <div className="text-[9px] text-zinc-400 font-mono truncate">GPS: {c.gps_device_id}</div>}
                {c.status === "disponivel" && (
                  <button
                    onClick={() => setQuickCar(c)}
                    className="w-full text-[11px] font-display font-bold py-2 bg-[#F5A623] hover:bg-[#D97706] text-white inline-flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`quick-rent-${c.plate}`}
                  >
                    <Rocket className="w-3.5 h-3.5" /> {t("fleet.rentNow")}
                  </button>
                )}
                <div className="flex gap-1.5 pt-1.5 border-t border-zinc-100">
                  <button onClick={() => openEdit(c)} className="flex-1 text-[10px] py-1 border border-zinc-200 hover:border-zinc-400 inline-flex items-center justify-center gap-1" data-testid={`edit-car-${c.plate}`}><Edit2 className="w-3 h-3" /> {t("common.edit")}</button>
                  <button onClick={() => remove(c.id)} className="text-[10px] py-1 px-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600" data-testid={`delete-car-${c.plate}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        );
      })()}
      <QuickRentalDialog
        open={!!quickCar}
        car={quickCar}
        onClose={() => setQuickCar(null)}
        onCreated={() => { setQuickCar(null); load(); }}
      />
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
      <label className="overline">{label}</label>
      {children}
    </div>
  );
}

function ActiveRenterBlock({ active, t }) {
  const { rental, customer, charges } = active;
  const returnDate = rental?.return_date ? parseISO(rental.return_date) : null;
  const today = new Date();
  const daysLeft = returnDate ? differenceInCalendarDays(returnDate, today) : null;
  const isOverdue = daysLeft != null && daysLeft < 0;
  const isDueSoon = daysLeft != null && daysLeft >= 0 && daysLeft <= 1;
  const accent = isOverdue ? "border-rose-400 bg-rose-50" : isDueSoon ? "border-amber-400 bg-amber-50" : "border-[#F5A623]/30 bg-[#F5A623]/5";
  const pendingTotal = ((charges?.tolls || 0) + (charges?.tickets || 0));
  return (
    <Link
      to={`/alugueis?open=${rental.id}`}
      className={`block border-l-2 ${accent} px-2 py-1.5 hover:bg-zinc-50 transition-colors`}
      data-testid={`active-renter-${rental.id}`}
    >
      <div className="flex items-start gap-1.5 mb-1">
        <User className="w-3 h-3 text-[#F5A623] mt-0.5 shrink-0" />
        <div className="font-display font-semibold text-[11px] leading-tight truncate flex-1">
          {customer?.name || t("fleet.unknownRenter")}
        </div>
      </div>
      {customer?.phone && (
        <div className="text-[9px] font-mono text-zinc-500 flex items-center gap-1 truncate pl-4">
          <Phone className="w-2.5 h-2.5" /> {customer.phone}
        </div>
      )}
      {rental.payment_status && (
        <div className="pl-4 mt-1">
          <PaymentStatusBadge paymentStatus={rental.payment_status} size="sm" showAmount />
        </div>
      )}
      {returnDate && (
        <div className="text-[9px] font-mono mt-0.5 pl-4 flex items-center gap-1">
          <CalendarIcon className="w-2.5 h-2.5" />
          <span className="text-zinc-500">{t("fleet.dueOn")}</span>{" "}
          <span className={isOverdue ? "text-rose-700 font-bold" : isDueSoon ? "text-amber-700 font-bold" : "text-zinc-700"}>
            {format(returnDate, "MMM dd")}
            {isOverdue && ` · ${Math.abs(daysLeft)}d ${t("fleet.overdue")}`}
            {!isOverdue && daysLeft === 0 && ` · ${t("fleet.today")}`}
            {!isOverdue && daysLeft === 1 && ` · ${t("fleet.tomorrow")}`}
          </span>
        </div>
      )}
      {charges && pendingTotal > 0 && (
        <div
          className="mt-1.5 pl-4 flex items-center justify-between text-[9px] font-mono border-t border-current/10 pt-1"
          data-testid={`pending-charges-${rental.id}`}
        >
          <span className="text-zinc-500 uppercase">{t("fleet.pendingCharges")}</span>
          <span className="font-bold text-rose-700">${pendingTotal.toFixed(2)}</span>
        </div>
      )}
      {charges && (charges.tolls > 0 || charges.tickets > 0) && (
        <div className="pl-4 flex gap-2 text-[9px] font-mono text-zinc-600 mt-0.5">
          {charges.tolls > 0 && <span>🛣️ ${charges.tolls.toFixed(2)}</span>}
          {charges.tickets > 0 && <span>⚠️ ${charges.tickets.toFixed(2)}</span>}
        </div>
      )}
    </Link>
  );
}

function ComplianceBadges({ car }) {
  const days = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };
  const items = [
    { key: "INS", d: days(car.insurance_expiry) },
    { key: "INS", d: days(car.ma_inspection_expiry), code: "MA" },
    { key: "REG", d: days(car.registration_expiry) },
  ].filter((i) => i.d !== null && i.d <= 30);
  if (items.length === 0) return null;
  return (
    <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
      {items.map((it, i) => {
        const expired = it.d < 0;
        const urgent = it.d <= 7;
        const cls = expired
          ? "bg-red-600 text-white border-red-700"
          : urgent
          ? "bg-amber-500 text-white border-amber-600"
          : "bg-zinc-900 text-amber-300 border-zinc-700";
        return (
          <div key={i} className={`text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${cls}`} title={it.key}>
            {it.code || it.key} {expired ? `-${Math.abs(it.d)}d` : `${it.d}d`}
          </div>
        );
      })}
    </div>
  );
}
